import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import type { Lesson, LessonStatus, LessonDrillRef } from '../types/lesson';
import { getUserProfile } from './userProfile';
import { sendMessage } from './chat';
import { getOrder, completeOrder } from './orders';

/**
 * Helper to convert Firestore timestamps to ISO strings
 */
function convertTimestamps(data: Record<string, unknown>): Record<string, unknown> {
  const result = { ...data };
  const timestampFields = ['createdAt', 'updatedAt', 'reservedAt', 'deliveredAt', 'playerViewedAt', 'submissionStartedAt'];

  for (const field of timestampFields) {
    if (result[field] && typeof result[field] === 'object' && 'toDate' in (result[field] as object)) {
      result[field] = (result[field] as Timestamp).toDate().toISOString();
    }
  }

  return result;
}


/**
 * Gets a lesson by ID
 */
export async function getLesson(lessonId: string): Promise<Lesson | null> {
  const lessonRef = doc(db, 'lessons', lessonId);
  const lessonSnap = await getDoc(lessonRef);

  if (!lessonSnap.exists()) {
    return null;
  }

  return convertTimestamps(lessonSnap.data()) as unknown as Lesson;
}

/**
 * Gets all lessons for a conversation
 */
export async function getLessonsForConversation(conversationId: string): Promise<Lesson[]> {
  const q = query(
    collection(db, 'lessons'),
    where('conversationId', '==', conversationId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => convertTimestamps(doc.data()) as unknown as Lesson);
}

/**
 * Subscribes to real-time updates for lessons in a conversation
 */
export function subscribeToLessonsForConversation(
  conversationId: string,
  callback: (lessons: Lesson[]) => void
): () => void {
  const q = query(
    collection(db, 'lessons'),
    where('conversationId', '==', conversationId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const lessons = snapshot.docs.map((doc) => convertTimestamps(doc.data()) as unknown as Lesson);
    callback(lessons);
  });
}

/**
 * Gets all lessons for a coach (Orders)
 */
export async function getCoachLessons(
  coachId: string,
  status?: LessonStatus
): Promise<Lesson[]> {
  let q;

  if (status) {
    q = query(
      collection(db, 'lessons'),
      where('coachId', '==', coachId),
      where('status', '==', status),
      orderBy('updatedAt', 'desc')
    );
  } else {
    q = query(
      collection(db, 'lessons'),
      where('coachId', '==', coachId),
      orderBy('updatedAt', 'desc')
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => convertTimestamps(doc.data()) as unknown as Lesson);
}

/**
 * Gets all lessons for an order, regardless of which coach delivered them.
 * Used to show the full lesson history in a transferred training program.
 */
export async function getLessonsForOrder(orderId: string): Promise<Lesson[]> {
  const snap = await getDocs(
    query(collection(db, 'lessons'), where('orderId', '==', orderId), orderBy('lessonNumber', 'asc'))
  );
  return snap.docs.map((d) => convertTimestamps(d.data()) as unknown as Lesson);
}

/**
 * Subscribes to real-time updates for coach lessons
 */
export function subscribeToCoachLessons(
  coachId: string,
  status: LessonStatus | undefined,
  callback: (lessons: Lesson[]) => void
): () => void {
  let q;

  if (status) {
    q = query(
      collection(db, 'lessons'),
      where('coachId', '==', coachId),
      where('status', '==', status),
      orderBy('updatedAt', 'desc')
    );
  } else {
    q = query(
      collection(db, 'lessons'),
      where('coachId', '==', coachId),
      orderBy('updatedAt', 'desc')
    );
  }

  return onSnapshot(q, (snapshot) => {
    const lessons = snapshot.docs.map((doc) => convertTimestamps(doc.data()) as unknown as Lesson);
    callback(lessons);
  });
}

/**
 * Gets all lessons for a player
 */
export async function getPlayerLessons(playerId: string): Promise<Lesson[]> {
  const q = query(
    collection(db, 'lessons'),
    where('playerId', '==', playerId),
    orderBy('updatedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => convertTimestamps(doc.data()) as unknown as Lesson);
}

/**
 * Subscribes to real-time updates for player lessons
 */
export function subscribeToPlayerLessons(
  playerId: string,
  callback: (lessons: Lesson[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(
    collection(db, 'lessons'),
    where('playerId', '==', playerId),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const lessons = snapshot.docs.map((doc) => convertTimestamps(doc.data()) as unknown as Lesson);
    callback(lessons);
  }, (err) => { if (onError) onError(err); else console.error('[subscribeToPlayerLessons]', err); });
}


export interface DeliverLessonOptions {
  lessonId: string;
  coachId: string;
  originalVideoId: string;
  publishedDraftId: string;
  coachNotes?: string;
  attachedDrills?: LessonDrillRef[];
}

/**
 * Delivers a lesson (coach action) — sets status to 'delivered'.
 * If all lessons in the order are now delivered, marks the order complete.
 */
export async function deliverLesson(options: DeliverLessonOptions): Promise<void> {
  const { lessonId, coachId, originalVideoId, publishedDraftId, coachNotes, attachedDrills } = options;

  const lesson = await getLesson(lessonId);
  if (!lesson) throw new Error('Lesson not found');
  if (lesson.coachId !== coachId) throw new Error('Not authorized');
  if (lesson.status !== 'in_progress') throw new Error('Lesson must be in_progress');

  const lessonRef = doc(db, 'lessons', lessonId);
  await updateDoc(lessonRef, {
    status: 'delivered',
    deliveredAt: serverTimestamp(),
    publishedOriginalVideoId: originalVideoId,
    publishedDraftId,
    ...(coachNotes && { coachNotes }),
    ...(attachedDrills && attachedDrills.length > 0 && { attachedDrills }),
    updatedAt: serverTimestamp(),
  });

  await sendMessage(
    lesson.conversationId,
    coachId,
    `Your lesson is ready!`,
    'lesson_delivered',
    undefined,
    lessonId
  );

  // Check if all lessons in the order are now delivered.
  // Use a query (getLessonsForOrder) rather than individual getDoc calls —
  // individual reads fail for transferred orders where some lesson docs still
  // carry the old coach's coachId, which the new coach can't read by ID.
  const order = await getOrder(lesson.orderId);
  if (order && order.status === 'active') {
    const totalExpected = order.totalLessons ?? order.lessonIds.length;
    const orderLessons = await getLessonsForOrder(lesson.orderId);
    // Count the just-delivered lesson as delivered (Firestore write may not be committed yet)
    const deliveredCount = orderLessons.filter(
      (l) => l.status === 'delivered' || l.id === lessonId
    ).length;
    if (deliveredCount >= totalExpected) {
      // Trigger backend renewal for recurring plans AND manual auto-renew opt-ins.
      // Both use the same recurringRenewalPending mechanism: the backend charges the
      // saved card and creates the next order after this flag is set.
      const shouldRenew = order.isRecurringPlan || order.autoRenewOptedIn;
      await completeOrder(order.id, { recurringRenewalPending: shouldRenew });
    }
  }
}


/**
 * Adds a review to a completed lesson
 */
export async function addLessonReview(
  lessonId: string,
  playerId: string,
  rating: number,
  comment?: string
): Promise<void> {
  const lesson = await getLesson(lessonId);
  if (!lesson) {
    throw new Error('Lesson not found');
  }

  if (lesson.playerId !== playerId) {
    throw new Error('Not authorized to review this lesson');
  }

  if (lesson.status !== 'delivered') {
    throw new Error('Can only review a delivered lesson');
  }

  // Check if review already exists in reviews collection
  const existingReviewQuery = query(
    collection(db, 'reviews'),
    where('lessonId', '==', lessonId)
  );
  const existingReviewSnap = await getDocs(existingReviewQuery);
  if (!existingReviewSnap.empty) {
    throw new Error('Lesson has already been reviewed');
  }

  // Get player profile for review details
  const playerUserProfile = await getUserProfile(playerId);
  if (!playerUserProfile) {
    throw new Error('Player profile not found');
  }

  // Get player name and avatar from playerProfile embedded object (or fallback to legacy fields)
  const playerName = playerUserProfile.playerProfile?.displayName ||
                     playerUserProfile.displayName ||
                     playerUserProfile.playerProfile?.username ||
                     playerUserProfile.username ||
                     'Player';
  const playerAvatarUrl = playerUserProfile.playerProfile?.avatarUrl || playerUserProfile.avatarUrl;

  // Fetch order for offering metadata
  const order = await getOrder(lesson.orderId);

  // Create review document in reviews collection (single source of truth)
  const reviewRef = doc(collection(db, 'reviews'));
  const reviewData = {
    id: reviewRef.id,
    lessonId,
    orderId: lesson.orderId,
    conversationId: lesson.conversationId,
    coachId: lesson.coachId,
    playerId,
    playerName,
    ...(playerAvatarUrl && { playerAvatarUrl }),
    ...(order?.offeringId && { offeringId: order.offeringId }),
    ...(order?.offeringTitle && { offeringTitle: order.offeringTitle }),
    rating,
    comment: comment || '',
    createdAt: serverTimestamp(),
  };

  await setDoc(reviewRef, reviewData);

  // Send review submitted message — fire-and-forget so a slow conversation
  // write on staging can't hang the modal after the review doc is committed.
  sendMessage(
    lesson.conversationId,
    playerId,
    `Left a ${rating}-star review for this lesson.`,
    'lesson_review_submitted',
    undefined,
    lessonId
  ).catch(err => console.error('[addLessonReview] sendMessage failed:', err));

  // The coach's average rating is recomputed on the server when the review
  // is created (onReviewCreated); the rules let no client write it.
}

/**
 * Mark a delivered lesson as viewed by the player.
 * Only sets playerViewedAt if it hasn't been set yet.
 */
export async function markLessonViewed(lessonId: string): Promise<void> {
  console.log('[markLessonViewed] called with lessonId:', lessonId);
  if (!lessonId) {
    console.error('[markLessonViewed] No lessonId provided!');
    return;
  }
  const lessonRef = doc(db, 'lessons', lessonId);
  const snap = await getDoc(lessonRef);
  if (!snap.exists()) {
    console.warn('[markLessonViewed] Lesson doc not found:', lessonId);
    return;
  }
  const data = snap.data();
  if (data.playerViewedAt) {
    console.log('[markLessonViewed] Already viewed, skipping:', lessonId);
    return;
  }
  console.log('[markLessonViewed] Writing playerViewedAt for:', lessonId);
  await updateDoc(lessonRef, { playerViewedAt: serverTimestamp() });
  console.log('[markLessonViewed] Successfully wrote playerViewedAt for:', lessonId);
}

